/**
 * useTimetableListScreen.js
 * -------------------------
 * State management hook for Education > Lectures > Time Table submodule.
 * Complies strictly with module_implementation_standards.md:
 * - Rule 87: Single Entry Point (global topbar Create New only, no local header add button).
 * - Rule 88: Zero native browser dialogs (appConfirm & appToast only).
 * - Rule 89: Background JobEngine for Excel/PDF export (useExportJob).
 * - Enterprise Print Engine: executePrintReport with 1,000-row guardrail.
 */
import { useEffect, useMemo, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  fetchTimetableList,
  deleteTimetableEntry,
  getTimetableOptions,
  fetchTimetableMasterOptions,
  DAYS_OPTIONS,
  TIMETABLE_SEARCH_FIELDS,
  generateLecturesFromTimetable,
} from '../api/timetableApi'
import { appToast } from '../../../../components/AppToast'
import { appConfirm } from '../../../../components/AppConfirmModal'
import { useExportJob } from '../../../../hooks/useExportJob'
import { executePrintReport } from '../../../../utils/printReportUtil'

export default function useTimetableListScreen() {
  const navigate = useNavigate()

  // Dynamic Master Options (Rule 90)
  const [dynamicOptions, setDynamicOptions] = useState({
    companies: [{ value: 'All', label: 'All Companies' }],
    branches: [{ value: 'All', label: 'All Branches' }],
    courses: [{ value: 'All', label: 'All Courses' }],
    batches: [{ value: 'All', label: 'All Batches' }],
    subjects: [{ value: 'All', label: 'All Subjects' }],
    faculties: [{ value: 'All', label: 'All Faculty' }],
    classrooms: [{ value: 'All', label: 'All Classrooms' }],
  })

  useEffect(() => {
    async function loadOptions() {
      try {
        const res = await fetchTimetableMasterOptions()
        setDynamicOptions({
          companies: [{ value: 'All', label: 'All Companies' }, ...res.companies],
          branches: [{ value: 'All', label: 'All Branches' }, ...res.branches],
          courses: [{ value: 'All', label: 'All Courses' }, ...res.courses],
          batches: [{ value: 'All', label: 'All Batches' }, ...res.batches],
          subjects: [{ value: 'All', label: 'All Subjects' }, ...res.subjects],
          faculties: [{ value: 'All', label: 'All Faculty' }, ...res.faculties],
          classrooms: [{ value: 'All', label: 'All Classrooms' }, ...res.classrooms],
        })
      } catch (err) {
        console.error('Failed to load timetable master options:', err)
      }
    }
    loadOptions()
  }, [])

  const options = useMemo(() => ({
    days: DAYS_OPTIONS,
    searchFields: TIMETABLE_SEARCH_FIELDS,
    companies: dynamicOptions.companies,
    branches: dynamicOptions.branches,
    courses: dynamicOptions.courses,
    batches: dynamicOptions.batches,
    subjects: dynamicOptions.subjects,
    faculties: dynamicOptions.faculties,
    classrooms: dynamicOptions.classrooms,
  }), [dynamicOptions])

  // Filter states
  const [searchField, setSearchField] = useState('All')
  const [searchText, setSearchText] = useState('')
  const [companyFilter, setCompanyFilter] = useState('All')
  const [branchFilter, setBranchFilter] = useState('All')
  const [courseFilter, setCourseFilter] = useState('All')
  const [dayFilter, setDayFilter] = useState('All')
  const [batchFilter, setBatchFilter] = useState('All')
  const [subjectFilter, setSubjectFilter] = useState('All')
  const [facultyFilter, setFacultyFilter] = useState('All')

  // Ordering & Pagination
  const [orderBy, setOrderBy] = useState('day')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  // Data states
  const [records, setRecords] = useState([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(false)

  // Modals & Drawers
  const [isReportsOpen, setIsReportsOpen] = useState(false)

  // Background Export Job Engine (Rule 89)
  const {
    triggerExport,
    isExporting,
  } = useExportJob({
    jobType: 'EXPORT',
    module: 'timetable',
  })

  // Fetch paginated records from live backend
  useEffect(() => {
    let isCancelled = false
    async function loadData() {
      try {
        setLoading(true)
        const res = await fetchTimetableList({
          dayFilter,
          searchField,
          searchText,
          companyFilter,
          branchFilter,
          courseFilter,
          facultyFilter,
          batchFilter,
          subjectFilter,
          orderBy,
          page: currentPage,
          pageSize,
        })
        if (!isCancelled && res) {
          setRecords(res.items || [])
          setTotal(res.total || 0)
          setTotalPages(res.totalPages || 1)
        }
      } catch (err) {
        if (!isCancelled) {
          console.error('Failed to fetch timetable records:', err)
          appToast.error('Failed to load timetable records.')
          setRecords([])
          setTotal(0)
          setTotalPages(1)
        }
      } finally {
        if (!isCancelled) {
          setLoading(false)
        }
      }
    }
    loadData()
    return () => {
      isCancelled = true
    }
  }, [
    dayFilter,
    searchField,
    searchText,
    companyFilter,
    branchFilter,
    courseFilter,
    facultyFilter,
    batchFilter,
    subjectFilter,
    orderBy,
    currentPage,
    pageSize,
    refreshTrigger,
  ])

  const resetFilters = useCallback(() => {
    setSearchField('All')
    setSearchText('')
    setCompanyFilter('All')
    setBranchFilter('All')
    setCourseFilter('All')
    setDayFilter('All')
    setBatchFilter('All')
    setSubjectFilter('All')
    setFacultyFilter('All')
    setCurrentPage(1)
  }, [])

  // Soft Delete with danger modal (Rule 88)
  const handleDelete = useCallback(
    async (id, faculty, dayOfWeek) => {
      const confirmed = await appConfirm({
        title: 'Delete Time Table Entry',
        message: `Are you sure you want to delete the schedule entry for ${faculty || 'this slot'} on ${dayOfWeek || 'selected day'}? This action cannot be undone.`,
        confirmLabel: 'Delete Entry',
        variant: 'danger',
      })
      if (!confirmed) return

      try {
        await deleteTimetableEntry(id)
        setRefreshTrigger((prev) => prev + 1)
        appToast.success('Timetable entry deleted successfully.')
      } catch (err) {
        console.error('Failed to delete timetable entry:', err)
        appToast.error('Failed to delete timetable entry.')
      }
    },
    [],
  )

  // Mass lecture generation
  const handleCreateLectures = useCallback(
    async (fromDate, tillDate) => {
      if (!fromDate || !tillDate) {
        appToast.error('Please select both From Date and Till Date.')
        return
      }
      try {
        const res = await generateLecturesFromTimetable(fromDate, tillDate)
        setIsCreateEntriesModalOpen(false)
        appToast.success(res.message || `${res.created_count || 0} lecture(s) created from timetable!`)
        setRefreshTrigger((prev) => prev + 1)
      } catch (err) {
        console.error('Failed to generate lectures:', err)
        const errMsg = err?.response?.data?.detail || 'Failed to generate lectures.'
        appToast.error(errMsg)
      }
    },
    [],
  )

  // Background export triggers (Rule 89)
  const handleExportExcel = useCallback(() => {
    triggerExport(
      {
        dayOfWeek: dayFilter,
        searchField,
        searchText,
        companyId: companyFilter !== 'All' ? companyFilter : undefined,
        branchId: branchFilter !== 'All' ? branchFilter : undefined,
        courseId: courseFilter !== 'All' ? courseFilter : undefined,
        facultyId: facultyFilter !== 'All' ? facultyFilter : undefined,
        batchId: batchFilter !== 'All' ? batchFilter : undefined,
        subjectId: subjectFilter !== 'All' ? subjectFilter : undefined,
      },
      'XLSX',
    )
  }, [
    triggerExport,
    dayFilter,
    searchField,
    searchText,
    companyFilter,
    branchFilter,
    courseFilter,
    facultyFilter,
    batchFilter,
    subjectFilter,
  ])

  const handleExportPdf = useCallback(() => {
    triggerExport(
      {
        dayOfWeek: dayFilter,
        searchField,
        searchText,
        companyId: companyFilter !== 'All' ? companyFilter : undefined,
        branchId: branchFilter !== 'All' ? branchFilter : undefined,
        courseId: courseFilter !== 'All' ? courseFilter : undefined,
        facultyId: facultyFilter !== 'All' ? facultyFilter : undefined,
        batchId: batchFilter !== 'All' ? batchFilter : undefined,
        subjectId: subjectFilter !== 'All' ? subjectFilter : undefined,
      },
      'PDF',
    )
  }, [
    triggerExport,
    dayFilter,
    searchField,
    searchText,
    companyFilter,
    branchFilter,
    courseFilter,
    facultyFilter,
    batchFilter,
    subjectFilter,
  ])

  return {
    state: {
      options,
      searchField,
      searchText,
      companyFilter,
      branchFilter,
      courseFilter,
      dayFilter,
      batchFilter,
      subjectFilter,
      facultyFilter,
      orderBy,
      currentPage,
      pageSize,
      records,
      total,
      totalPages,
      loading,
      isExporting,
    },
    actions: {
      setSearchField,
      setSearchText,
      setCompanyFilter,
      setBranchFilter,
      setCourseFilter,
      setDayFilter,
      setBatchFilter,
      setSubjectFilter,
      setFacultyFilter,
      setOrderBy,
      setCurrentPage,
      setPageSize,
      resetFilters,
      handleDelete,
      handleCreateLectures,
      handleExportExcel,
      handleExportPdf,
      reload: () => setRefreshTrigger((p) => p + 1),
    },
  }
}
